/**
 * Creature mesh wrapper — clones a glTF skeleton, plays animations from
 * aiState. Mirrors the Player wrapper in `../player.ts` but simpler (no
 * tinting, no death freeze logic — creature death just removes the entity).
 *
 * Animation clip lookup uses fuzzy name matching because Quaternius packs
 * vary slightly between species (some have "Idle" + "Walk" + "Run", others
 * "Idle_A" / "Walk_Cycle" / etc). We try common prefixes.
 */

import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkeleton } from "three/examples/jsm/utils/SkeletonUtils.js";
import { CREATURE_DEFS, type CreatureType } from "@tribez/shared";
import { toToon, addCharacterOutline } from "../toon";

const CROSSFADE_SEC = 0.18;

export type CreatureAction = "idle" | "wander" | "flee" | "chase" | "attack" | "dead";

export interface CreatureMesh {
  object: THREE.Object3D;
  mixer: THREE.AnimationMixer;
  setAction(action: CreatureAction): void;
  setRotation(angleY: number): void;
  dispose(): void;
}

function findClip(animations: THREE.AnimationClip[], names: string[]): THREE.AnimationClip | null {
  for (const n of names) {
    const c = THREE.AnimationClip.findByName(animations, n);
    if (c) return c;
  }
  // Fuzzy: case-insensitive prefix match
  for (const clip of animations) {
    const lower = clip.name.toLowerCase();
    for (const n of names) {
      if (lower.startsWith(n.toLowerCase())) return clip;
    }
  }
  return null;
}

export function createCreatureMesh(gltf: GLTF, type: string): CreatureMesh {
  const root = cloneSkeleton(gltf.scene);
  // Scale lives on the registry row (CREATURE_DEFS[type].scale). Single source
  // of truth; client doesn't carry its own SCALE table.
  const def = CREATURE_DEFS[type as CreatureType];
  root.scale.setScalar(def?.scale ?? 1.0);

  // Convert source materials to MeshToonMaterial so creatures share the
  // cel-shaded look. Same toon gradient ramp as doodads / buildings / players.
  root.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    const src = mesh.material as THREE.Material | THREE.Material[];
    if (Array.isArray(src)) {
      mesh.material = src.map((mm) => toToon(mm, { heroLighting: true }));
    } else if (src) {
      mesh.material = toToon(src, { heroLighting: true });
    }
  });

  // Inverted-hull outline. Width is now WORLD-SPACE (addCharacterOutline divides
  // out def.scale + any internal node scale), so every creature gets the same
  // constant outline thickness regardless of size — fixes the over-thick outline
  // on large prehistoric meshes (Cave Bear 2.0×, Sabertooth 1.33×).
  addCharacterOutline(root, 0.035);

  const mixer = new THREE.AnimationMixer(root);
  const idleClip = findClip(gltf.animations, ["Idle", "Idle_A", "Eating"]);
  const walkClip = findClip(gltf.animations, ["Walk", "Walk_Cycle", "Walking"]);
  const runClip  = findClip(gltf.animations, ["Run", "Gallop", "Running"]) ?? walkClip;
  const deathClip = findClip(gltf.animations, ["Death", "Die", "Dead"]);

  // If no idle, fall back to the first clip — unanimated freeze is worse than nothing
  const fallback = gltf.animations[0];
  const idle = mixer.clipAction(idleClip ?? fallback!);
  const walk = walkClip ? mixer.clipAction(walkClip) : idle;
  const run  = runClip  ? mixer.clipAction(runClip)  : walk;
  const death = deathClip ? mixer.clipAction(deathClip) : null;
  if (death) {
    death.setLoop(THREE.LoopOnce, 1);
    death.clampWhenFinished = true;
  }
  idle.play();

  let current: CreatureAction = "idle";

  function actionFor(act: CreatureAction): THREE.AnimationAction {
    if (act === "flee" || act === "chase") return run;
    if (act === "wander") return walk;
    if (act === "dead" && death) return death;
    return idle;
  }

  return {
    object: root,
    mixer,
    setAction(action) {
      if (action === current) return;
      const from = actionFor(current);
      const to = actionFor(action);
      to.reset().play();
      to.crossFadeFrom(from, CROSSFADE_SEC, true);
      current = action;
    },
    setRotation(angleY) {
      root.rotation.y = angleY;
    },
    dispose() {
      mixer.stopAllAction();
      mixer.uncacheRoot(root);
      root.removeFromParent();
    },
  };
}
